import { db } from "../../firebase/config";
import { useState, useEffect } from "react";
import {
  collection, addDoc, getDocs, doc, updateDoc,
  query, orderBy, serverTimestamp
} from "firebase/firestore";

import { C, F, Label, Rule, Btn, Input, Select, Spinner, KPI } from "../ui";
import { fmtUSD } from "../../utils/ddp";

const ESTADOS_CICLO = [
  { value: "abierto",     label: "Abierto — recibiendo pedidos" },
  { value: "cerrado",     label: "Cerrado — en producción" },
  { value: "embarcado",   label: "Embarcado" },
  { value: "en_aduana",   label: "En aduana" },
  { value: "finalizado",  label: "Finalizado — entregado" },
];

const ESTADO_COLOR = {
  abierto:    "#3D7A5F",
  cerrado:    "#8B6E3A",
  embarcado:  "#60A5FA",
  en_aduana:  "#A855F7",
  finalizado: "#555",
};



const EMPTY_CICLO = {
  nombre: "",
  origen: "Guangzhou",
  tipo_contenedor: "40HC",
  cbm_max: 68,
  kg_max: 26000,
  fecha_cierre: "",
  fecha_zarpe: "",
  fecha_arribo: "",
  notas: "",
};

// ── Barra de llenado ──────────────────────────────────────────────────────────
function LoadBar({ val, max, label, warn = 0.85 }) {
  const p = Math.min(val / max, 1);
  const col = p >= 1 ? C.red : p >= warn ? C.amber : C.white;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <Label>{label}</Label>
        <span style={{ fontFamily: F.mono, fontSize: 11, color: col }}>
          {val.toFixed(2)} / {max} ({(p * 100).toFixed(1)}%)
        </span>
      </div>
      <div style={{ height: "1px", background: C.border2 }}>
        <div style={{ height: "1px", width: `${p * 100}%`, background: col, transition: "width .5s ease" }} />
      </div>
    </div>
  );
}

// ── Formulario nuevo ciclo ────────────────────────────────────────────────────
function FormCiclo({ onSave, onCancel }) {
  const [form, setForm] = useState(EMPTY_CICLO);
  const [saving, setSaving] = useState(false);
  const set = f => v => setForm(p => ({ ...p, [f]: v }));

  const save = async () => {
    if (!form.nombre || !form.fecha_cierre) return;
    setSaving(true);
    await addDoc(collection(db, "ciclos"), {
      ...form,
      cbm_max: parseFloat(form.cbm_max),
      kg_max: parseFloat(form.kg_max),
      estado: "abierto",
      cbm_usado: 0,
      kg_usado: 0,
      pedidos_count: 0,
      fob_total: 0,
      creadoEn: serverTimestamp(),
    });
    setSaving(false);
    onSave();
  };

  return (
    <div style={{ background: C.s1, border: `0.5px solid ${C.border}`, padding: 28 }}>
      <div style={{ fontFamily: F.serif, fontSize: 20, color: C.white, marginBottom: 24 }}>
        Nuevo ciclo de contenedor
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Input label="Nombre del ciclo" value={form.nombre} onChange={set("nombre")}
          placeholder="Ciclo 07/2025 — Guangzhou" />
        <Input label="Puerto de origen" value={form.origen} onChange={set("origen")}
          placeholder="Guangzhou" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
        <Select label="Tipo de contenedor" value={form.tipo_contenedor} onChange={set("tipo_contenedor")}
          options={[{ value:"40HC", label:"40HC — 68 CBM" }, { value:"20ft", label:"20ft — 28 CBM" }]} />
        <Input label="CBM máximo" value={form.cbm_max} onChange={set("cbm_max")} type="number" />
        <Input label="Peso máximo (kg)" value={form.kg_max} onChange={set("kg_max")} type="number" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
        <div>
          <Label style={{ marginBottom: 6 }}>Fecha cierre de pedidos</Label>
          <input type="date" value={form.fecha_cierre}
            onChange={e => set("fecha_cierre")(e.target.value)}
            style={{ width: "100%", background: "transparent", border: "none", borderBottom: `0.5px solid ${C.border2}`, color: C.white, fontFamily: F.sans, fontSize: 13, padding: "7px 0", outline: "none" }} />
        </div>
        <div>
          <Label style={{ marginBottom: 6 }}>Fecha estimada de zarpe</Label>
          <input type="date" value={form.fecha_zarpe}
            onChange={e => set("fecha_zarpe")(e.target.value)}
            style={{ width: "100%", background: "transparent", border: "none", borderBottom: `0.5px solid ${C.border2}`, color: C.white, fontFamily: F.sans, fontSize: 13, padding: "7px 0", outline: "none" }} />
        </div>
        <div>
          <Label style={{ marginBottom: 6 }}>Fecha estimada arribo BA</Label>
          <input type="date" value={form.fecha_arribo}
            onChange={e => set("fecha_arribo")(e.target.value)}
            style={{ width: "100%", background: "transparent", border: "none", borderBottom: `0.5px solid ${C.border2}`, color: C.white, fontFamily: F.sans, fontSize: 13, padding: "7px 0", outline: "none" }} />
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Label style={{ marginBottom: 6 }}>Notas internas</Label>
        <textarea value={form.notas} onChange={e => set("notas")(e.target.value)}
          placeholder="Notas para el equipo..."
          rows={2}
          style={{ width: "100%", background: "transparent", border: `0.5px solid ${C.border2}`, color: C.white, fontFamily: F.sans, fontSize: 12, padding: 10, outline: "none", resize: "vertical" }} />
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <Btn variant="solid" onClick={save} disabled={saving || !form.nombre || !form.fecha_cierre}
          style={{ flex: 1, justifyContent: "center" }}>
          {saving ? "Creando..." : "Crear ciclo →"}
        </Btn>
        <Btn variant="ghost" onClick={onCancel}>Cancelar</Btn>
      </div>
    </div>
  );
}

// ── Detalle de un ciclo ───────────────────────────────────────────────────────
function DetalleCiclo({ ciclo, onClose, onUpdate }) {
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cambiandoEstado, setCambiandoEstado] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const snap = await getDocs(
        query(collection(db, "pedidos"), orderBy("creadoEn", "desc"))
      );
      const todos = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setPedidos(todos.filter(p => p.ciclo_id === ciclo.id));
      setLoading(false);
    };
    load();
  }, [ciclo.id]);

  const cambiarEstado = async (nuevoEstado) => {
    setCambiandoEstado(true);
    await updateDoc(doc(db, "ciclos", ciclo.id), {
      estado: nuevoEstado,
      updatedAt: serverTimestamp(),
    });
    onUpdate();
    setCambiandoEstado(false);
  };




  // Agrupar pedidos por proveedor para el PO
  const porProveedor = pedidos.reduce((acc, p) => {
    (p.items || []).forEach(item => {
      const prov = item.proveedor || "Sin proveedor";
      if (!acc[prov]) acc[prov] = { items: [], fob_total: 0 };
      acc[prov].items.push(item);
      acc[prov].fob_total += (item.fob_compra || 0) * (item.qty || 0);
    });
    return acc;
  }, {});

  return (
    <div style={{ background: C.s1, border: `0.5px solid ${C.border}` }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px", borderBottom: `0.5px solid ${C.border}` }}>
        <div>
          <div style={{ fontFamily: F.serif, fontSize: 22, color: C.white, marginBottom: 4 }}>{ciclo.nombre}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: ESTADO_COLOR[ciclo.estado] }} />
            <span style={{ fontFamily: F.mono, fontSize: 9, color: ESTADO_COLOR[ciclo.estado], letterSpacing: 1.5, textTransform: "uppercase" }}>
              {ESTADOS_CICLO.find(e => e.value === ciclo.estado)?.label || ciclo.estado}
            </span>
          </div>
        </div>
        <button onClick={onClose} style={{ fontFamily: F.mono, fontSize: 18, color: C.muted, background: "transparent", border: "none", cursor: "pointer" }}>×</button>
      </div>

      <div style={{ padding: 24 }}>
        {/* KPIs */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "0.5px", background: C.border, marginBottom: 24 }}>
          <KPI label="Pedidos activos" value={ciclo.pedidos_count || 0} />
          <KPI label="FOB total" value={fmtUSD(ciclo.fob_total || 0)} />
          <KPI label="CBM ocupado" value={`${(ciclo.cbm_usado || 0).toFixed(2)} m³`} sub={`de ${ciclo.cbm_max} disponibles`} />
          <KPI label="Cierre pedidos" value={ciclo.fecha_cierre || "—"} />
        </div>

        {/* Barras */}
        <LoadBar val={ciclo.cbm_usado || 0} max={ciclo.cbm_max} label="Llenado volumétrico (CBM)" />
        <LoadBar val={ciclo.kg_usado || 0} max={ciclo.kg_max} label="Peso total (KG)" />

        <Rule />

        {/* Fechas */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16, marginBottom: 24 }}>
          {[["Cierre de pedidos", ciclo.fecha_cierre], ["Zarpe estimado", ciclo.fecha_zarpe], ["Arribo BA estimado", ciclo.fecha_arribo]].map(([l, v]) => (
            <div key={l}>
              <Label style={{ marginBottom: 4 }}>{l}</Label>
              <div style={{ fontFamily: F.mono, fontSize: 13, color: v ? C.off : C.muted }}>{v || "Sin definir"}</div>
            </div>
          ))}
        </div>

        <Rule />

        {/* Cambiar estado */}
        <div style={{ marginBottom: 24 }}>
          <Label style={{ marginBottom: 12 }}>Avanzar estado del ciclo</Label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {ESTADOS_CICLO.map(e => (
              <button key={e.value}
                onClick={() => cambiarEstado(e.value)}
                disabled={ciclo.estado === e.value || cambiandoEstado}
                style={{
                  padding: "6px 14px", fontFamily: F.sans, fontSize: 9,
                  letterSpacing: 1.5, textTransform: "uppercase", cursor: "pointer",
                  background: ciclo.estado === e.value ? ESTADO_COLOR[e.value] : "transparent",
                  color: ciclo.estado === e.value ? C.bg : ESTADO_COLOR[e.value],
                  border: `0.5px solid ${ESTADO_COLOR[e.value]}`,
                  opacity: cambiandoEstado ? 0.5 : 1, transition: "all .2s",
                }}>{e.label.split(" —")[0]}</button>
            ))}
          </div>
        </div>

        <Rule />

        {/* Pedidos del ciclo */}
        <div style={{ marginBottom: 24 }}>
          <Label style={{ marginBottom: 16 }}>Pedidos en este ciclo</Label>
          {loading ? <Spinner /> : pedidos.length === 0 ? (
            <div style={{ fontFamily: F.sans, fontSize: 12, color: C.muted, padding: "16px 0" }}>
              Sin pedidos confirmados en este ciclo aún.
            </div>
          ) : (
            <div style={{ background: C.border }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 120px 100px 100px 80px", background: C.s2, padding: "8px 14px", gap: 8 }}>
                {["Cliente", "FOB Total", "CBM", "Estado", ""].map(h => (
                  <div key={h} style={{ fontFamily: F.sans, fontSize: 8, letterSpacing: 2, color: C.muted, textTransform: "uppercase" }}>{h}</div>
                ))}
              </div>
              {pedidos.map(p => (
                <div key={p.id} style={{ display: "grid", gridTemplateColumns: "1fr 120px 100px 100px 80px", background: C.s1, padding: "12px 14px", gap: 8, borderBottom: `0.5px solid ${C.border}`, alignItems: "center" }}>
                  <div>
                    <div style={{ fontFamily: F.sans, fontSize: 12, color: C.white }}>{p.cliente_nombre || "—"}</div>
                    <div style={{ fontFamily: F.mono, fontSize: 9, color: C.muted, marginTop: 2 }}>{p.cliente_email || ""}</div>
                  </div>
                  <div style={{ fontFamily: F.mono, fontSize: 11, color: C.off }}>{fmtUSD(p.fob_total)}</div>
                  <div style={{ fontFamily: F.mono, fontSize: 11, color: C.off }}>{(p.cbm_total || 0).toFixed(2)} m³</div>
                  <div style={{ fontFamily: F.mono, fontSize: 9, color: ESTADO_COLOR[p.estado] || C.muted, textTransform: "uppercase" }}>{p.estado || "—"}</div>
                  <div style={{ fontFamily: F.mono, fontSize: 9, color: C.muted }}>{p.items?.length || 0} ítems</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Resumen por proveedor */}
        {Object.keys(porProveedor).length > 0 && (
          <>
            <Rule />
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <Label>Resumen por proveedor — Purchase Order</Label>
                <Btn variant="solid" size="sm">Exportar PO →</Btn>
              </div>
              {Object.entries(porProveedor).map(([prov, data]) => (
                <div key={prov} style={{ marginBottom: 12, padding: "14px 16px", background: C.s2, border: `0.5px solid ${C.border}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontFamily: F.serif, fontSize: 15, color: C.white }}>{prov}</div>
                    <div style={{ fontFamily: F.mono, fontSize: 12, color: C.off }}>{fmtUSD(data.fob_total)}</div>
                  </div>
                  <div style={{ fontFamily: F.mono, fontSize: 9, color: C.muted, marginTop: 4 }}>
                    {data.items.length} líneas de pedido
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Notas */}
        {ciclo.notas && (
          <>
            <Rule />
            <div>
              <Label style={{ marginBottom: 8 }}>Notas internas</Label>
              <div style={{ fontFamily: F.sans, fontSize: 12, color: C.muted, lineHeight: 1.6 }}>{ciclo.notas}</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── COMPONENTE PRINCIPAL ──────────────────────────────────────────────────────
export default function AdminCiclos() {
  const [ciclos, setCiclos]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [selected, setSelected]     = useState(null);
  const [filtro, setFiltro]         = useState("todos");

  const fetch = async () => {
    setLoading(true);
    const snap = await getDocs(query(collection(db, "ciclos"), orderBy("creadoEn", "desc")));
    setCiclos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => { fetch(); }, []);



  const filtered = filtro === "todos" ? ciclos : ciclos.filter(c => c.estado === filtro);

  const cicloActivo = ciclos.find(c => c.estado === "abierto");

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <div style={{ fontFamily: F.serif, fontSize: 24, color: C.white, marginBottom: 4 }}>Ciclos de contenedor</div>
          <div style={{ fontFamily: F.mono, fontSize: 10, color: C.muted }}>
            {ciclos.length} ciclos · {ciclos.filter(c => c.estado === "abierto").length} abierto
          </div>
        </div>
        {!showForm && (
          <Btn variant="solid" onClick={() => setShowForm(true)}>+ Nuevo ciclo</Btn>
        )}
      </div>

      {/* Alerta ciclo activo */}
      {cicloActivo && !showForm && !selected && (
        <div style={{ background: "#0A1A0A", border: `0.5px solid ${C.green}`, padding: "14px 20px", marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontFamily: F.sans, fontSize: 12, color: C.green, marginBottom: 4 }}>
              ● Ciclo activo — {cicloActivo.nombre}
            </div>
            <div style={{ fontFamily: F.mono, fontSize: 10, color: C.muted }}>
              Cierre: {cicloActivo.fecha_cierre || "sin definir"} · {(cicloActivo.cbm_usado || 0).toFixed(1)} / {cicloActivo.cbm_max} CBM · {cicloActivo.pedidos_count || 0} pedidos
            </div>
          </div>
          <Btn variant="green" size="sm" onClick={() => setSelected(cicloActivo)}>Ver detalle →</Btn>
        </div>
      )}

      {/* Formulario nuevo ciclo */}
      {showForm && (
        <div style={{ marginBottom: 24 }}>
          <FormCiclo onSave={() => { setShowForm(false); fetch(); }} onCancel={() => setShowForm(false)} />
        </div>
      )}

      {/* Detalle ciclo seleccionado */}
      {selected && (
        <div style={{ marginBottom: 24 }}>
          <DetalleCiclo
            ciclo={selected}
            onClose={() => setSelected(null)}
            onUpdate={() => { fetch(); setSelected(null); }}
          />
        </div>
      )}

      {/* Filtros */}
      {!selected && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {["todos", "abierto", "cerrado", "embarcado", "en_aduana", "finalizado"].map(f => (
              <button key={f} onClick={() => setFiltro(f)} style={{
                padding: "4px 12px", fontFamily: F.sans, fontSize: 9,
                letterSpacing: 1.5, textTransform: "uppercase", cursor: "pointer",
                background: filtro === f ? C.white : "transparent",
                color: filtro === f ? C.bg : C.muted,
                border: `0.5px solid ${filtro === f ? C.white : C.border}`,
                transition: "all .2s",
              }}>{f}</button>
            ))}
          </div>

          {/* Lista ciclos */}
          {loading ? <Spinner /> : (
            <div style={{ background: C.border }}>
              {filtered.length === 0 && (
                <div style={{ background: C.s1, padding: 32, fontFamily: F.sans, fontSize: 12, color: C.muted, textAlign: "center" }}>
                  {ciclos.length === 0 ? "Sin ciclos creados. Creá el primero." : "Sin ciclos en este estado."}
                </div>
              )}
              {filtered.map(c => {
                const cbmPct = c.cbm_max > 0 ? (c.cbm_usado || 0) / c.cbm_max : 0;
                return (
                  <div key={c.id}
                    onClick={() => setSelected(c)}
                    style={{ background: C.s1, padding: "18px 20px", borderBottom: `0.5px solid ${C.border}`, cursor: "pointer", transition: "background .15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = C.s2}
                    onMouseLeave={e => e.currentTarget.style.background = C.s1}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                          <div style={{ width: 6, height: 6, borderRadius: "50%", background: ESTADO_COLOR[c.estado], flexShrink: 0 }} />
                          <div style={{ fontFamily: F.serif, fontSize: 16, color: C.white }}>{c.nombre}</div>
                        </div>
                        <div style={{ fontFamily: F.mono, fontSize: 9, color: C.muted, letterSpacing: 1 }}>
                          {c.origen} · {c.tipo_contenedor} · {c.pedidos_count || 0} pedidos · FOB {fmtUSD(c.fob_total || 0)}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontFamily: F.mono, fontSize: 18, color: cbmPct >= 0.85 ? C.amber : C.white }}>
                          {(cbmPct * 100).toFixed(0)}%
                        </div>
                        <div style={{ fontFamily: F.mono, fontSize: 9, color: C.muted }}>llenado</div>
                      </div>
                    </div>

                    {/* Mini barra */}
                    <div style={{ height: "1px", background: C.border2 }}>
                      <div style={{
                        height: "1px",
                        width: `${cbmPct * 100}%`,
                        background: cbmPct >= 0.85 ? C.amber : ESTADO_COLOR[c.estado],
                        transition: "width .5s",
                      }} />
                    </div>

                    <div style={{ display: "flex", gap: 24, marginTop: 10 }}>
                      {[["Cierre", c.fecha_cierre], ["Zarpe", c.fecha_zarpe], ["Arribo BA", c.fecha_arribo]].map(([l, v]) => (
                        <div key={l}>
                          <div style={{ fontFamily: F.sans, fontSize: 8, color: C.muted, letterSpacing: 1.5, textTransform: "uppercase" }}>{l}</div>
                          <div style={{ fontFamily: F.mono, fontSize: 10, color: v ? C.off : C.muted }}>{v || "—"}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
